/**
 * Handle multiple file uploads and their preview before sending them
 *
 */

interface UploadFiles {
  [key: string]: File
}

export class FileUploader {
  private errorContainer: HTMLElement;
  private uploadFiles: UploadFiles;
  private studioFilesGrid: HTMLElement;
  private studioFilesInput: HTMLInputElement;
  private mobileImageInput;
  private removeFileButton: NodeList;
  private editedFiles: HTMLInputElement;
  private maxSubmitSize: number;

  constructor(form, errorContainer) {
    this.studioFilesInput = form.querySelector('input[name="studioFiles[]"]');
    this.mobileImageInput = form.querySelector('input[capture]') as HTMLInputElement;
    if (this.mobileImageInput.capture !== undefined) {
      this.mobileImageInput.closest('.mobile-image').classList.remove('hidden');
    }
    this.studioFilesGrid = form.querySelector('.js-file-grid');
    this.editedFiles = this.studioFilesGrid.querySelector('[name="editedFiles"]');
    this.removeFileButton = form.querySelectorAll('.js-uploader-remove-file');
    this.errorContainer = errorContainer;
    this.maxSubmitSize = 30000000; // should be less than 32mb Stage limit for post request size
    this.uploadFiles = {};
  }

  /**
   * Attach event listeners to elements to file inputs
   */
  public attachListeners() {
  this.studioFilesInput.addEventListener('change', this.generatePreview.bind(this), false);
  if (this.mobileImageInput.capture !== undefined) {
    this.mobileImageInput.addEventListener('change', this.generatePreview.bind(this), false);
  }

  this.removeFileButton?.forEach(button => {
    button.addEventListener('click', function(e) {
      e.preventDefault()
      const target = <HTMLInputElement>e.target
      const file = target.getAttribute('data-file')
      const filename = target.getAttribute('data-filename')
      const fieldname = target.getAttribute('data-fieldname')
      if (confirm(`Remove file: ${filename} ?`) === true) {
        const filesSingle = target.closest('.js-files-single')

        if (this.editedFiles && this.editedFiles.value) {
          const editedFilesArray = JSON.parse(this.editedFiles.value)
          // Remove file from edited files input
          editedFilesArray[fieldname] = editedFilesArray[fieldname].filter(f => f !== file)
          // Save changed array to edited files input
          this.editedFiles.value = JSON.stringify(editedFilesArray)
        }
        // Remove HTML element
        filesSingle.remove()
      }
    }.bind(this))
  })
}

  /**
   * Generate thumbnails and other info to grid
   * @param {Event} e
   */
     public generatePreview(e) {
      const fileList: FileList = e.target.files;
      const fileReaders = []

      for (let i = 0; i < fileList.length; i++) {
        const fileObject = fileList[i];
        fileReaders.push(this.readFile(fileObject));
      }
      Promise.all(fileReaders)
    }

  /**
  * Read every file added to file input
  * @param {File} file
  * @returns {Promise}
  */
  public readFile(file) {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader()
      fileReader.onload = (event) => {
        const img = new Image()
        img.onload = () => {

          this.errorContainer.innerHTML = '';
          if (this.uploadFiles[file.name + file.lastModified]) {
            this.throwError(`File ${file.name} is already selected.`);
            return
          } else {
            this.uploadFiles[file.name + file.lastModified] = file;
          }
          // Column
          const col = document.createElement('div')
          col.classList.add('col-xs-12', 'col-sm-6', 'col-md-4', 'col-xl-3', 'files-field__item', 'js-local-file')
          // Canvas
          const canvas = document.createElement('canvas')
          if (file.type === 'application/pdf') {
            canvas.classList.add('icon-image')
          }
          canvas.width = img.width
          canvas.height = img.height
          const context = canvas.getContext('2d')
          context.drawImage(img, 0, 0)
          // Texts
          const texts = document.createElement('b')
          texts.innerText = file.name;
          // Buttons
          const buttons = document.createElement('div')
          buttons.classList.add('display-flex', 'justify-content--between', 'files-field__item-buttons')
          const button = document.createElement('button')
          button.classList.add('button', 'button--link', 'button--link-red', 'js-remove-btn')
          button.innerText = 'Remove'

          button.addEventListener('click', function(e) {

            if (this.editedFiles && this.editedFiles.value) {
              this.studioFilesGrid.querySelectorAll('.js-remove-btn')?.forEach((btn,i) => {
                if (btn === e.target) this.removeStudioReference(i);
              })
            }
            this.errorContainer.innerHTML = '';
            delete this.uploadFiles[file.name + file.lastModified];

            // remove the image for the preview box
            col.remove()

            // remove the image from the HTML inputs
            this.removeFileFromInput(file);

          }.bind(this))

          // Append all together
          buttons.append(button)
          col.append(canvas)
          col.append(texts)
          col.append(buttons)
          this.studioFilesGrid.append(col)
        }
        if (file.type.includes('image')) {
          img.src = event.target.result as string
        } else {
          img.src = 'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiA/PjxzdmcgaWQ9IkxheWVyXzEiIHN0eWxlPSJlbmFibGUtYmFja2dyb3VuZDpuZXcgMCAwIDUwIDUwOyIgdmVyc2lvbj0iMS4xIiB2aWV3Qm94PSIwIDAgNTAgNTAiIHhtbDpzcGFjZT0icHJlc2VydmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPjxnIGlkPSJMYXllcl8xXzFfIj48cGF0aCBkPSJNMTIuODY2LDQ3LjI0YzMuMSwwLDYuMDE1LTEuMjA3LDguMjA3LTMuMzk5bDI0Ljg5NC0yNC44OTRjMy42OTgtMy42OTgsMy42OTgtOS43MTYsMC0xMy40MTQgICBjLTMuNjk3LTMuNjk4LTkuNzE3LTMuNjk4LTEzLjQxNCwwTDEwLjQxOCwyNy42NjhjLTEuMzkxLDEuMzkxLTIuMTU3LDMuMjQxLTIuMTU4LDUuMjA4czAuNzY1LDMuODE2LDIuMTU2LDUuMjA3ICAgczMuMjQsMi4xNTcsNS4yMDgsMi4xNTdjMS45NjcsMCwzLjgxNi0wLjc2Niw1LjIwNy0yLjE1N2wxOC4xMzYtMTguMTM2bC0xLjQxNC0xLjQxNEwxOS40MTcsMzYuNjY5ICAgYy0xLjAxMywxLjAxMy0yLjM2LDEuNTcxLTMuNzk1LDEuNTcxYy0xLjQzMywwLTIuNzc5LTAuNTU4LTMuNzkyLTEuNTcxcy0xLjU3LTIuMzYtMS41Ny0zLjc5MmMwLjAwMS0xLjQzNCwwLjU2LTIuNzgxLDEuNTcyLTMuNzk1ICAgTDMzLjk2Nyw2Ljk0N2MyLjkxOC0yLjkxOCw3LjY2OC0yLjkxOCwxMC41ODYsMHMyLjkxOCw3LjY2NywwLDEwLjU4NkwxOS42NTksNDIuNDI3Yy0xLjgxNCwxLjgxNC00LjIyOCwyLjgxMy02Ljc5MywyLjgxMyAgIGMtNS4yOTcsMC05LjYwNi00LjMxLTkuNjA2LTkuNjA2YzAtMi41NjYsMC45OTktNC45NzksMi44MTMtNi43OTNMMjguOTY3LDUuOTQ3bC0xLjQxNC0xLjQxNEw0LjY1OSwyNy40MjcgICBjLTIuMTkyLDIuMTkxLTMuMzk5LDUuMTA2LTMuMzk5LDguMjA3QzEuMjYsNDIuMDMzLDYuNDY3LDQ3LjI0LDEyLjg2Niw0Ny4yNHoiLz48L2c+PC9zdmc+'
        }
      }
      fileReader.readAsDataURL(file)
    });
  }

  /**
  * Remove the file from the input element's filelist using the file name as identifier
  * @param {file} file
  */
  private removeFileFromInput (file: File) {
    let fileIsRemoved = false;
    const updatedFiles = (fileSet) => {
      const dt = new DataTransfer();
      for (let i = 0; i < fileSet.length; i++) {
        if (fileSet[i].name === file.name && fileSet[i].lastModified === file.lastModified) {
          fileIsRemoved = true;
        } else {
          dt.items.add(fileSet[i])
        }
      }
      return dt.files
    }

    this.studioFilesInput.files = updatedFiles(this.studioFilesInput.files);
    if (!fileIsRemoved) {
      this.mobileImageInput.files = updatedFiles(this.mobileImageInput.files);
    }
  }

  /**
  * Display the error mesage
  * @param {message} error message
  */
  private async throwError(message) {
    this.errorContainer.innerHTML = message;
  }

  /**
  * Checks if the total size of the files doesn't exceed the Stage limit
  * @returns {boolean} does
  */
  public hasValidSize() {
    const size = Object.values(this.uploadFiles)
      .map(file => file?.size || 0)
      .reduce((acc, cur) => acc + cur);
    return size < this.maxSubmitSize;
  }

  /**
  * Return the current set of selected files
  * @returns {FileList} uploadFiles
  */
  public getFiles () {
    return this.uploadFiles
  }

  /**
  * Clear the uploadFiles object
  */
  public clearFiles () {
    this.uploadFiles = {}
  }

  /**
  *  Remove a studio reference for files that are not yet synced with the backend
  *  and do not have a data attribute with studio reference
  *  @param {number} index
  */
  private removeStudioReference (index) {
    const filesArray = JSON.parse(this.editedFiles.value).studioFiles || [];
    const updatedFiles = filesArray.filter((el, i) => i !== index);
    this.editedFiles.value = `{"studioFiles":${JSON.stringify(updatedFiles)}}`;
  }


  /**
  *  Update editedFiles hidden input value following an ajax request (e.g. save draft response)
  *  without that the client code doesn't know about the saved draft files
  * @param {string[]} newFiles
  */
  public updateFilesData (newFiles: Array<string>) {
    const jsonFiles = JSON.stringify(newFiles);
    if (this.editedFiles && this.editedFiles.value) {
      this.editedFiles.value = `{"studioFiles":${jsonFiles}}`;
    }
  }
}
